import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, SPACING, TYPOGRAPHY } from "@store/theme/design";
import * as React from "react"; // Store Custom Code
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    bannerText = "bannerText",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.bannerText]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    session: getSession(state),
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    currentLocation: getLocation(state),
});

const mapDispatchToProps = {};

type Props = OwnProps & HasHistory & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface LoginStatusBarStyles {
    wrapper?: InjectableCss;
    buttonsWrapper?: InjectableCss;
    signInClickable?: ClickablePresentationProps;
    modal?: ModalPresentationProps;
    signInButton?: ButtonPresentationProps;
    ignoreButton?: ButtonPresentationProps;
    titleText?: TypographyProps;
}

export const partNumberStyles: LoginStatusBarStyles = {
    buttonsWrapper: {
        css: css`
            margin: 1.25em 0 0 0;
            text-align: center;
        `,
    },
    signInClickable: {
        css: css`
            display: inline;
            text-decoration: underline;

            &:hover {
                text-decoration: none;
            }
        `,
    },
    modal: {
        size: 500,
        cssOverrides: {
            modalContent: css`
                padding: 2em 1em;
            `,
            modalTitle: css`
                padding: 10px 10px 10px 20px;
            `,
            headlineTypography: css`
                font-size: 20px;
                margin-bottom: 0;
            `,
        },
    },
    ignoreButton: {
        variant: "tertiary",
        css: css`
            margin-bottom: 1em;
        `,
    },
    signInButton: {
        css: css`
            margin-bottom: 1em;
        `,
    },
    titleText: { variant: "h2" },
};

const styles = partNumberStyles;

const LoginStatusBar: React.FC<Props> = ({ fields, history, session, signInUrl, currentLocation }) => {
    const isAuthenticated = session && session.isAuthenticated && !session.rememberMe && !session.isGuest;
    const isRememberMe = session && !session.isAuthenticated && session.rememberMe && !session.isGuest;
    const [modalIsOpen, setModalIsOpen] = React.useState(false);

    if (isAuthenticated && !isRememberMe) {
        return null;
    }

    const modalCloseHandler = () => {
        setModalIsOpen(false);
        setCookie("ignoreRememberMe", "true", { expires: 1 });
    };

    const onModalSignInHandler = (e: React.MouseEvent) => {
        onSignInHandler(e);
        setModalIsOpen(false);
        setCookie("ignoreRememberMe", "true", { expires: 1 });
    };

    const onSignInHandler = (e: React.MouseEvent) => {
        if (!signInUrl) {
            history.push("/MyAccount/SignIn");
        } else {
            history.push(`${signInUrl}?returnUrl=${currentLocation.pathname}${currentLocation.search}`);
        }
        e.preventDefault();
    };

    React.useEffect(() => {
        if (isRememberMe && !getCookie("ignoreRememberMe")) {
            setModalIsOpen(true);
        }
    }, []);

    return (
        <>
            <Modal
                {...styles.modal}
                headline={translate("Inactivity time reached")}
                isOpen={modalIsOpen}
                handleClose={modalCloseHandler}
            >
                <StyledWrapper>
                    {isRememberMe && (
                        <>
                            <Typography {...styles.titleText}>
                                {translate(
                                    "You have requested to remain logged in and can still browse the website normally.",
                                )}
                            </Typography>
                            <Typography>
                                {translate(
                                    "A new connection will be required to complete your shopping or view your sensitive account data.",
                                )}
                            </Typography>
                            <br />
                            <StyledWrapper {...styles.buttonsWrapper}>
                                <Button {...styles.signInButton} onClick={onModalSignInHandler}>
                                    {translate("Sign in now")}
                                </Button>
                                &nbsp;&nbsp;
                                <Button {...styles.ignoreButton} onClick={modalCloseHandler}>
                                    {translate("Keep shopping")}
                                </Button>
                            </StyledWrapper>
                        </>
                    )}
                </StyledWrapper>
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(LoginStatusBar)),
    definition: {
        displayName: "Login Status Bar With Popup",
        group: "Basic",
    },
};

export default widgetModule;
